import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Award, Plus, CheckCircle2, Clock, XCircle, Shield, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  active: { icon: CheckCircle2, color: 'text-[#A8E6CF]', bg: 'bg-[#A8E6CF]/10', label: 'Active' },
  expired: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-400/10', label: 'Expired' },
  pending: { icon: Clock, color: 'text-amber-400', bg: 'bg-amber-400/10', label: 'Pending' },
};

export default function CaregiverCertifications() {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    name: '', issuing_body: '', certificate_number: '', issue_date: '', expiry_date: ''
  });

  const fetchCerts = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/caregiver-management/certifications`, { withCredentials: true });
      setCerts(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCerts(); }, []);

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    try {
      await axios.post(`${API_URL}/api/caregiver-management/certifications`, form, { withCredentials: true });
      toast.success('Certification added');
      setForm({ name: '', issuing_body: '', certificate_number: '', issue_date: '', expiry_date: '' });
      setShowAdd(false);
      fetchCerts();
    } catch (err) {
      toast.error('Failed to add certification');
    }
  };

  const verified = certs.filter(c => c.verified);
  const unverified = certs.filter(c => !c.verified);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="caregiver-certifications-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">My Certifications</h1>
            <p className="text-sm text-muted-foreground mt-1">Professional qualifications and certificates</p>
          </div>
          <Button onClick={() => setShowAdd(!showAdd)} className="btn-primary gap-2" data-testid="add-cert-btn">
            <Plus className="w-4 h-4" /> Add Certification
          </Button>
        </div>

        {showAdd && (
          <Card className="glass-card border-border">
            <CardContent className="p-4 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <Input placeholder="Certification Name *" value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  className="bg-muted border-border" data-testid="cert-name-input" />
                <Input placeholder="Issuing Body" value={form.issuing_body}
                  onChange={e => setForm({ ...form, issuing_body: e.target.value })}
                  className="bg-muted border-border" data-testid="cert-issuer-input" />
                <Input placeholder="Certificate Number" value={form.certificate_number}
                  onChange={e => setForm({ ...form, certificate_number: e.target.value })}
                  className="bg-muted border-border" data-testid="cert-number-input" />
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-xs text-muted-foreground mb-1 block">Issue Date</label>
                    <Input type="date" value={form.issue_date}
                      onChange={e => setForm({ ...form, issue_date: e.target.value })}
                      className="bg-muted border-border" data-testid="cert-issue-date" />
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-muted-foreground mb-1 block">Expiry Date</label>
                    <Input type="date" value={form.expiry_date}
                      onChange={e => setForm({ ...form, expiry_date: e.target.value })}
                      className="bg-muted border-border" data-testid="cert-expiry-date" />
                  </div>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
                <Button onClick={handleAdd} className="btn-primary" data-testid="save-cert-btn">Save</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="glass-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-foreground">{certs.length}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-[#A8E6CF]">{verified.length}</p>
              <p className="text-xs text-muted-foreground">Verified</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-400">{unverified.length}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </CardContent>
          </Card>
        </div>

        {/* Certifications List */}
        <Card className="glass-card border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
              <Award className="w-5 h-5 text-[#FF6F61]" /> Certifications
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {certs.length === 0 ? (
              <div className="p-8 text-center">
                <Award className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No certifications yet</p>
                <p className="text-xs text-muted-foreground mt-1">Add your professional qualifications above</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {certs.map((cert, i) => {
                  const now = new Date().toISOString();
                  const isExpired = cert.expiry_date && cert.expiry_date < now;
                  const statusKey = isExpired ? 'expired' : cert.status || 'active';
                  const cfg = STATUS_CONFIG[statusKey] || STATUS_CONFIG.active;
                  const StatusIcon = cfg.icon;
                  return (
                    <div key={i} className="p-4 flex items-center gap-4 hover:bg-muted/30 transition-colors"
                      data-testid={`cert-item-${i}`}>
                      <div className={`p-2 rounded-lg ${cfg.bg} flex-shrink-0`}>
                        <StatusIcon className={`w-5 h-5 ${cfg.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-foreground font-medium text-sm">{cert.name}</p>
                          {cert.verified && (
                            <Shield className="w-4 h-4 text-[#00A6A0]" />
                          )}
                        </div>
                        {cert.issuing_body && (
                          <p className="text-xs text-muted-foreground">{cert.issuing_body}</p>
                        )}
                        {cert.certificate_number && (
                          <p className="text-xs text-muted-foreground font-mono">#{cert.certificate_number}</p>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        {cert.expiry_date && (
                          <p className={`text-xs ${isExpired ? 'text-red-400' : 'text-muted-foreground'}`}>
                            {isExpired ? 'Expired' : 'Expires'}: {cert.expiry_date.split('T')[0]}
                          </p>
                        )}
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                          cert.verified ? 'bg-[#00A6A0]/10 text-[#00A6A0]' : 'bg-amber-400/10 text-amber-400'
                        }`}>
                          {cert.verified ? 'Verified' : 'Pending Review'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
